import React, { useEffect, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSort, FaSortUp, FaSortDown, FaSortAlphaDown, FaSortAlphaUp } from 'react-icons/fa';
import { FiCheck, FiChevronDown } from 'react-icons/fi';
import './Filter.css';

const OPTIONS = [
  { value: 'pos', label: 'Position', Icon: FaSort },
  { value: 'prixCroissent', label: 'Prix (Croissant)', Icon: FaSortUp },
  { value: 'prixDecroissent', label: 'Prix (Décroissant)', Icon: FaSortDown },
  { value: 'nomA', label: 'Nom [A-Z]', Icon: FaSortAlphaDown },
  { value: 'nomZ', label: 'Nom [Z-A]', Icon: FaSortAlphaUp },
];

const panelVariants = {
  hidden: { opacity: 0, y: -8, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.22, ease: [0.16, 1, 0.3, 1], when: 'beforeChildren', staggerChildren: 0.035 },
  },
  exit: { opacity: 0, y: -6, scale: 0.98, transition: { duration: 0.15, ease: 'easeIn' } },
};

const optionVariants = {
  hidden: { opacity: 0, x: -8 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.18 } },
};

const Filter = ({ functionFilter }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState('pos');
  const [indicator, setIndicator] = useState({ opacity: 0, top: 0, height: 0 });

  const rootRef = useRef(null);
  const listRef = useRef(null);
  const rowRefs = useRef({});

  const selected = OPTIONS.find((o) => o.value === selectedValue) ?? OPTIONS[0];

  const handleFilter = (value) => {
    setSelectedValue(value);
    setIsOpen(false);
    if (typeof functionFilter === 'function') {
      functionFilter(value);
    }
  };

  const moveIndicatorTo = (el) => {
    if (!listRef.current || !el) return;
    const parentRect = listRef.current.getBoundingClientRect();
    const rect = el.getBoundingClientRect();
    setIndicator({ opacity: 1, top: rect.top - parentRect.top, height: rect.height });
  };

  useEffect(() => {
    if (!isOpen) return;
    const node = rowRefs.current[selectedValue];
    if (node) moveIndicatorTo(node);
  }, [isOpen, selectedValue]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  return (
    <Container className="filter-modern-container">
      <div className="filter-modern" ref={rootRef}>
        <motion.button
          type="button"
          className="filter-header"
          onClick={() => setIsOpen((prev) => !prev)}
          initial={{ y: -12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <selected.Icon className="filter-icon" />
          <span className="filter-text">
            <span className="filter-eyebrow">Trier par</span>
            <span className="filter-label">{selected.label}</span>
          </span>
          <motion.span
            className="filter-arrow"
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <FiChevronDown />
          </motion.span>
        </motion.button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              className="filter-options-container"
              variants={panelVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <div
                className="filter-options"
                ref={listRef}
                onMouseLeave={() => {
                  const node = rowRefs.current[selectedValue];
                  if (node) moveIndicatorTo(node);
                }}
              >
                <motion.span
                  className="filter-indicator"
                  animate={indicator}
                  transition={{ type: 'spring', stiffness: 420, damping: 32 }}
                />

                {OPTIONS.map(({ value, label, Icon }) => {
                  const isActive = value === selectedValue;
                  return (
                    <motion.div
                      key={value}
                      ref={(el) => {
                        rowRefs.current[value] = el;
                      }}
                      className={`filter-option ${isActive ? 'active' : ''}`}
                      variants={optionVariants}
                      onMouseEnter={(e) => moveIndicatorTo(e.currentTarget)}
                      onClick={() => handleFilter(value)}
                    >
                      <Icon className="filter-icon" />
                      <span>{label}</span>
                      {isActive && <FiCheck className="filter-check" />}
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Container>
  );
};

export default Filter;
